import React, { useState } from "react";
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { asJSONSafe } from "../functions";
import { formatDateAsDateTime } from "../functions/formatters";

const PREVIEW_MAX_LENGTH = 140;
const ID_PREVIEW_LENGTH = 12;

const wrapperStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 260,
    maxWidth: 420,
};

const rowStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "8px 10px",
    borderRadius: 6,
    background: "rgba(255, 255, 255, 0.72)",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
};

const rowTopStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
};

const systemNameStyle = {
    fontWeight: 600,
    fontSize: 12,
    color: "#1a2332",
    letterSpacing: 0.2,
    minWidth: 52,
};

const metaStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    fontSize: 11,
    color: "#5a6570",
    lineHeight: 1.35,
};

const metaItemStyle = {
    whiteSpace: "nowrap",
};

const idCollapsedStyle = {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: 11,
    color: "#1565c0",
    maxWidth: 160,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
    border: "none",
    background: "none",
    padding: 0,
    textAlign: "left",
};

const idExpandedWrapStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxWidth: "100%",
};

const idExpandedStyle = {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: 11,
    color: "#37474f",
    wordBreak: "break-all",
    whiteSpace: "normal",
    userSelect: "all",
};

const idActionsStyle = {
    display: "flex",
    gap: 8,
    alignItems: "center",
};

const errorsPreviewStyle = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    whiteSpace: "normal",
    lineHeight: 1.35,
    maxHeight: "2.7em",
    fontSize: 11,
    color: "#b71c1c",
};

const linkButtonStyle = {
    marginTop: 2,
    padding: 0,
    border: "none",
    background: "none",
    color: "#1565c0",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 500,
    textDecoration: "underline",
    whiteSpace: "nowrap",
};

const errorListStyle = {
    margin: 0,
    paddingLeft: 18,
};

const errorItemStyle = {
    marginBottom: 10,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    fontSize: 13,
    lineHeight: 1.4,
};

const emptyStyle = {
    fontSize: 12,
    color: "#78909c",
};

const getUniqueErrors = (errors) => {
    if (!errors) return [];
    const list = Array.isArray(errors) ? errors : [errors];
    return [...new Set(list.filter((e) => e != null && String(e).trim() !== "").map(String))];
};

const truncateId = (id) => {
    const value = String(id);
    if (value.length <= ID_PREVIEW_LENGTH + 3) return value;
    return `${value.slice(0, ID_PREVIEW_LENGTH)}…`;
};

const formatIds = (ids) => {
    if (!ids?.length) return null;
    const full = ids.join(", ");
    const preview = ids.map(truncateId).join(", ");
    return { full, preview };
};

const StatusChip = ({ received }) => (
    <Chip
        size="small"
        label={received ? "Отправлен" : "Не отправлен"}
        color={received ? "success" : "default"}
        variant={received ? "filled" : "outlined"}
        sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 500,
            "& .MuiChip-label": { px: 0.9 },
        }}
    />
);

const ErrorsBlock = ({ errors }) => {
    const [open, setOpen] = useState(false);
    const uniqueErrors = getUniqueErrors(errors);
    if (!uniqueErrors.length) return null;

    const firstError = uniqueErrors[0];
    const isLong = uniqueErrors.length > 1 || firstError.length > PREVIEW_MAX_LENGTH;
    const preview =
        firstError.length > PREVIEW_MAX_LENGTH
            ? `${firstError.slice(0, PREVIEW_MAX_LENGTH)}…`
            : firstError;

    return (
        <>
            <div>
                <div style={errorsPreviewStyle} title={firstError}>
                    {preview}
                </div>
                {isLong && (
                    <button
                        type="button"
                        style={linkButtonStyle}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setOpen(true);
                        }}
                    >
                        Подробнее{uniqueErrors.length > 1 ? ` (${uniqueErrors.length})` : ""}
                    </button>
                )}
            </div>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="md"
                fullWidth
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle>Ошибки</DialogTitle>
                <DialogContent dividers>
                    <ul style={errorListStyle}>
                        {uniqueErrors.map((err, index) => (
                            <li key={`${index}-${err.slice(0, 24)}`} style={errorItemStyle}>
                                {err}
                            </li>
                        ))}
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const IdsBlock = ({ ids }) => {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const idsInfo = formatIds(ids);
    if (!idsInfo) return null;

    const isTruncated = idsInfo.full !== idsInfo.preview;

    const copyId = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(idsInfo.full);
            } else {
                const textarea = document.createElement("textarea");
                textarea.value = idsInfo.full;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (_) {
            setExpanded(true);
        }
    };

    if (!isTruncated) {
        return (
            <div style={idExpandedWrapStyle}>
                <span style={idExpandedStyle}>ID: {idsInfo.full}</span>
                <button type="button" style={linkButtonStyle} onClick={copyId}>
                    {copied ? "Скопировано" : "Копировать"}
                </button>
            </div>
        );
    }

    if (!expanded) {
        return (
            <button
                type="button"
                style={idCollapsedStyle}
                title="Показать полный ID"
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setExpanded(true);
                }}
            >
                ID: {idsInfo.preview}
            </button>
        );
    }

    return (
        <div style={idExpandedWrapStyle}>
            <span style={idExpandedStyle}>ID: {idsInfo.full}</span>
            <div style={idActionsStyle}>
                <button type="button" style={linkButtonStyle} onClick={copyId}>
                    {copied ? "Скопировано" : "Копировать"}
                </button>
                <button
                    type="button"
                    style={linkButtonStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setExpanded(false);
                    }}
                >
                    Свернуть
                </button>
            </div>
        </div>
    );
};

const ReceiverRow = ({ name, received, receivedTime, ids, errors }) => {
    const hasIds = !!ids?.length;
    const timeLabel = receivedTime ? formatDateAsDateTime(receivedTime) : null;

    return (
        <div style={rowStyle}>
            <div style={rowTopStyle}>
                <span style={systemNameStyle}>{name}</span>
                <StatusChip received={!!received} />
            </div>
            <div style={metaStyle}>
                {timeLabel && <span style={metaItemStyle}>{timeLabel}</span>}
                {hasIds && <IdsBlock ids={ids} />}
                {!timeLabel && !hasIds && (
                    <span style={metaItemStyle}>Нет времени / ID</span>
                )}
            </div>
            <ErrorsBlock errors={errors} />
        </div>
    );
};

export default function ProtocolReceiversSection({ data }) {
    if (!data) return <span></span>;

    const receivers = asJSONSafe(data);

    const hasEHealthData = receivers?.EHealthReceived || receivers?.EHealthErrors;
    const hasLmkData = receivers?.LmkReceived || receivers?.LmkErrors;
    const hasESenData = receivers?.ESenReceived || receivers?.ESenErrors;

    if (!hasEHealthData && !hasLmkData && !hasESenData) {
        return <span style={emptyStyle}>Нет данных о получателях</span>;
    }

    return (
        <div style={wrapperStyle}>
            {hasEHealthData && (
                <ReceiverRow
                    name="EHealth"
                    received={receivers?.EHealthReceived}
                    receivedTime={receivers?.EHealthReceivedTime}
                    ids={receivers?.EHealthIds}
                    errors={receivers?.EHealthErrors}
                />
            )}
            {hasLmkData && (
                <ReceiverRow
                    name="Lmk"
                    received={receivers?.LmkReceived}
                    receivedTime={receivers?.LmkReceivedTime}
                    ids={receivers?.LmkIds}
                    errors={receivers?.LmkErrors}
                />
            )}
            {hasESenData && (
                <ReceiverRow
                    name="ESen"
                    received={receivers?.ESenReceived}
                    receivedTime={receivers?.ESenReceivedTime}
                    ids={receivers?.ESenIds}
                    errors={receivers?.ESenErrors}
                />
            )}
        </div>
    );
}
